import { useState, useRef, useEffect, useCallback } from 'react';
import type { CountryCode } from 'libphonenumber-js';
import { api } from '@/services/api';
import { ApiError } from '@/services/httpClient';
import { translateForLanguage, type Language } from '@/contexts/LanguageContext';
import { resolveCandidateLocale } from '@/lib/candidateLocale';
import type { ApplicationDocumentField, Company, JobPosition, JobPosting } from '@/types';
import {
  buildSocialProfileSubmissions,
  initialSocialProfileValues,
  resolveSocialProfilesFromPosition,
  socialProfileFieldKey,
  socialProfileLabel,
  validateSocialProfileUrl,
  type SocialProfileField,
} from '@/lib/socialProfiles';
import {
  documentFieldLabel,
  multipartFieldNameForDocumentType,
  resolveDocumentFieldsFromPosition,
} from '@/lib/documentFields';
import {
  defaultPhoneCountry,
  formatPhoneE164,
  isValidLocalPhoneNumber,
} from '@/lib/phoneCountries';

const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;

type FormState = 'loading' | 'not_found' | 'closed' | 'form' | 'success';

function isValidPdfFile(file: File): boolean {
  return file.type === 'application/pdf' && file.size <= MAX_DOCUMENT_BYTES;
}

export interface UsePublicApplicationReturn {
  pageState: FormState;
  pageLanguage: Language;
  tPage: (key: string, params?: Record<string, string | number>) => string;
  submitting: boolean;
  errors: Record<string, string>;
  serverError: string;
  company: Company | null;
  position: JobPosition | null;
  posting: JobPosting | null;
  documentFields: ApplicationDocumentField[];
  documentFiles: Record<string, File | null>;
  setDocumentFile: (multipartFieldName: string, file: File | null) => void;
  socialProfiles: SocialProfileField[];
  socialValues: Record<string, string>;
  setSocialValue: (key: string, value: string) => void;
  firstName: string;
  setFirstName: (v: string) => void;
  lastName: string;
  setLastName: (v: string) => void;
  email: string;
  setEmail: (v: string) => void;
  phoneCountry: CountryCode;
  setPhoneCountry: (v: CountryCode) => void;
  phoneNumber: string;
  setPhoneNumber: (v: string) => void;
  gdprConsent: boolean;
  setGdprConsent: (v: boolean) => void;
  aiConsent: boolean;
  setAiConsent: (v: boolean) => void;
  documentFieldLabelFor: (field: ApplicationDocumentField) => string;
  socialProfileLabelFor: (field: SocialProfileField) => string;
  openDocumentPicker: (multipartFieldName: string) => void;
  registerDocumentInput: (multipartFieldName: string, el: HTMLInputElement | null) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  handleDocumentFileDrop: (multipartFieldName: string, e: React.DragEvent) => void;
  handleDocumentFileSelect: (multipartFieldName: string, e: React.ChangeEvent<HTMLInputElement>) => void;
}

export function usePublicApplication(companySlug: string | undefined, postingSlug: string | undefined): UsePublicApplicationReturn {
  const documentInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const [pageState, setPageState] = useState<FormState>('loading');
  const [candidateLocale, setCandidateLocale] = useState<Language>('en');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState('');

  const [company, setCompany] = useState<Company | null>(null);
  const [position, setPosition] = useState<JobPosition | null>(null);
  const [posting, setPosting] = useState<JobPosting | null>(null);
  const [documentFields, setDocumentFields] = useState<ApplicationDocumentField[]>([]);
  const [documentFiles, setDocumentFiles] = useState<Record<string, File | null>>({});
  const [socialProfiles, setSocialProfiles] = useState<SocialProfileField[]>([]);
  const [socialValues, setSocialValues] = useState<Record<string, string>>({});

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneCountry, setPhoneCountry] = useState<CountryCode>('IT');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [gdprConsent, setGdprConsent] = useState(false);
  const [aiConsent, setAiConsent] = useState(false);

  const pageLanguage = resolveCandidateLocale(posting, position, company);
  const effectivePageLanguage = pageState === 'loading' || pageState === 'not_found'
    ? candidateLocale
    : pageLanguage;

  const tPage = useCallback(
    (key: string, params?: Record<string, string | number>) =>
      translateForLanguage(effectivePageLanguage, key, params),
    [effectivePageLanguage],
  );

  const documentFieldLabelFor = useCallback(
    (field: ApplicationDocumentField) => documentFieldLabel(field, tPage),
    [tPage],
  );

  const socialProfileLabelFor = useCallback(
    (field: SocialProfileField) => socialProfileLabel(field, tPage),
    [tPage],
  );

  const setSocialValue = useCallback((key: string, value: string) => {
    setSocialValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setDocumentFile = useCallback((multipartFieldName: string, file: File | null) => {
    setDocumentFiles((prev) => ({ ...prev, [multipartFieldName]: file }));
  }, []);

  const registerDocumentInput = useCallback((multipartFieldName: string, el: HTMLInputElement | null) => {
    documentInputRefs.current[multipartFieldName] = el;
  }, []);

  const openDocumentPicker = useCallback((multipartFieldName: string) => {
    documentInputRefs.current[multipartFieldName]?.click();
  }, []);

  useEffect(() => {
    setPhoneCountry(defaultPhoneCountry(effectivePageLanguage));
  }, [effectivePageLanguage]);

  useEffect(() => {
    if (!companySlug || !postingSlug) {
      setPageState('not_found');
      return;
    }
    api.public.getPosting(companySlug, postingSlug).then(data => {
      if (!data) { setPageState('not_found'); return; }
      const locale = resolveCandidateLocale(data.posting, data.position, data.company);
      setCandidateLocale(locale);
      const configuredSocials =
        data.position.socialProfiles?.length
          ? data.position.socialProfiles
          : resolveSocialProfilesFromPosition(data.position);
      const configuredDocuments = resolveDocumentFieldsFromPosition(data.position);
      setCompany(data.company);
      setPosition(data.position);
      setPosting(data.posting);
      setSocialProfiles(configuredSocials);
      setSocialValues(initialSocialProfileValues(configuredSocials));
      setDocumentFields(configuredDocuments);
      setDocumentFiles(Object.fromEntries(
        configuredDocuments.map((field) => [multipartFieldNameForDocumentType(field.type), null]),
      ));
      setPageState(data.posting.isActive ? 'form' : 'closed');
    }).catch(() => setPageState('not_found'));
  }, [companySlug, postingSlug]);

  const validate = useCallback(() => {
    const errs: Record<string, string> = {};
    if (!firstName.trim()) errs.firstName = tPage('apply.firstNameRequired');
    if (!lastName.trim()) errs.lastName = tPage('apply.lastNameRequired');
    if (!email.trim()) errs.email = tPage('apply.emailRequired');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = tPage('apply.emailInvalid');

    if (position?.requirePhone && !phoneNumber.trim()) {
      errs.phone = tPage('apply.phoneRequired');
    } else if (phoneNumber.trim() && !isValidLocalPhoneNumber(phoneCountry, phoneNumber)) {
      errs.phone = tPage('apply.phoneInvalid');
    }

    for (const field of socialProfiles) {
      const key = socialProfileFieldKey(field);
      const value = socialValues[key]?.trim() ?? '';
      if (field.required && !value) {
        errs[key] = tPage('apply.socialRequired', { label: socialProfileLabel(field, tPage) });
      } else if (value && !validateSocialProfileUrl(field.type, value)) {
        errs[key] = tPage('apply.socialInvalid', { label: socialProfileLabel(field, tPage) });
      }
    }

    for (const field of documentFields) {
      const multipartName = multipartFieldNameForDocumentType(field.type);
      const file = documentFiles[multipartName];
      const label = documentFieldLabel(field, tPage);
      if (field.required && !file) {
        errs[multipartName] = tPage('apply.documentRequired', { label });
      } else if (file && !isValidPdfFile(file)) {
        errs[multipartName] = tPage('apply.documentInvalid', { label });
      }
    }

    if (!gdprConsent) errs.gdprConsent = tPage('apply.gdprConsentRequired');
    if (!aiConsent) errs.aiConsent = tPage('apply.aiConsentRequired');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [firstName, lastName, email, phoneCountry, phoneNumber, documentFields, documentFiles, gdprConsent, aiConsent, position, socialProfiles, socialValues, tPage]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError('');
    if (!validate() || !posting || !position || !company) return;

    setSubmitting(true);
    try {
      const phoneE164 = formatPhoneE164(phoneCountry, phoneNumber);
      const consentTimestamp = new Date().toISOString();
      const submissions = buildSocialProfileSubmissions(socialProfiles, socialValues);
      const linkedIn = submissions.find((s) => s.type === 'linkedin')?.url;

      const formData = new FormData();
      formData.append('firstName', firstName);
      formData.append('lastName', lastName);
      formData.append('email', email);
      if (phoneE164) formData.append('phone', phoneE164);
      if (linkedIn) formData.append('linkedinUrl', linkedIn);
      if (submissions.length > 0) {
        formData.append('socialProfiles', JSON.stringify(submissions));
      }
      formData.append('jobPostingId', posting.id);
      formData.append('companyId', company.id);
      formData.append('jobPositionId', position.id);
      formData.append('source', 'landing-page');
      formData.append('gdprConsentTimestamp', consentTimestamp);
      formData.append('aiConsentTimestamp', consentTimestamp);

      for (const field of documentFields) {
        const multipartName = multipartFieldNameForDocumentType(field.type);
        const file = documentFiles[multipartName];
        if (file) formData.append(multipartName, file);
      }

      await api.public.submitApplication(formData);
      setPageState('success');
    } catch (err: unknown) {
      const apiErr = err instanceof ApiError ? err : null;
      const msg = apiErr?.message ?? (err instanceof Error ? err.message : '');

      if (apiErr?.status === 429 || msg.includes('rate')) {
        setServerError(tPage('apply.rateLimit'));
      } else if (apiErr?.status === 409 || apiErr?.code === 'CONFLICT' || msg.includes('duplicate')) {
        setServerError(msg || tPage('apply.duplicate'));
      } else if (msg.includes('phone')) {
        setErrors(prev => ({ ...prev, phone: tPage('apply.phoneRequired') }));
      } else if (msg.includes('socialProfiles') || msg.includes('Social profile')) {
        setServerError(tPage('apply.validationError'));
      } else if (
        apiErr?.status === 400 ||
        apiErr?.code === 'VALIDATION_ERROR' ||
        msg.includes('Document') ||
        msg.includes('document') ||
        msg.includes('VALIDATION_ERROR')
      ) {
        setServerError(msg || tPage('apply.validationError'));
      } else if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
        setServerError(tPage('apply.networkError'));
      } else {
        setServerError(msg || tPage('apply.genericError'));
      }
    } finally {
      setSubmitting(false);
    }
  }, [validate, posting, position, company, firstName, lastName, email, phoneCountry, phoneNumber, socialProfiles, socialValues, documentFields, documentFiles, gdprConsent, aiConsent, tPage]);

  const handleDocumentFileDrop = useCallback((multipartFieldName: string, e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && isValidPdfFile(file)) {
      setDocumentFile(multipartFieldName, file);
      setErrors(prev => { const n = { ...prev }; delete n[multipartFieldName]; return n; });
    }
  }, [setDocumentFile]);

  const handleDocumentFileSelect = useCallback((multipartFieldName: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && isValidPdfFile(file)) {
      setDocumentFile(multipartFieldName, file);
      setErrors(prev => { const n = { ...prev }; delete n[multipartFieldName]; return n; });
    }
  }, [setDocumentFile]);

  return {
    pageState, pageLanguage: effectivePageLanguage, tPage, submitting, errors, serverError,
    company, position, posting,
    documentFields, documentFiles, setDocumentFile,
    socialProfiles, socialValues, setSocialValue,
    firstName, setFirstName, lastName, setLastName,
    email, setEmail,
    phoneCountry, setPhoneCountry, phoneNumber, setPhoneNumber,
    gdprConsent, setGdprConsent, aiConsent, setAiConsent,
    documentFieldLabelFor, socialProfileLabelFor,
    openDocumentPicker, registerDocumentInput,
    handleSubmit, handleDocumentFileDrop, handleDocumentFileSelect,
  };
}
